<template>
  <div>
    <slot name="label">
      <!-- eslint-disable-next-line vuejs-accessibility/label-has-for -->
      <label
        v-if="label != null && label.length > 0"
        :for="componentId"
        class="w-full text-sm"
        :class="{ capitalize: !preserveCase }"
      >
        <strong>{{ label }}</strong>
        <!-- Screen readers skip the asterisk, so the word carries it. -->
        <template v-if="required">
          <span class="text-rhino" aria-hidden="true">&nbsp;*</span>
          <span class="sr-only">{{ $t('frontend/required') }}</span>
        </template>
      </label>
    </slot>
    <div class="relative flex items-stretch w-full">
      <slot name="input-left"></slot>
      <!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label -->
      <input
        :id="componentId"
        :type="type"
        class="block w-full bg-transparent ps-0 pe-8 pt-0 pb-2 border-b border-solid border-rhino rounded-none outline-none"
        :class="inputClass"
        :placeholder="placeholder"
        :value="modelValue"
        :autocomplete="autocomplete"
        :aria-required="required ? 'true' : undefined"
        :aria-invalid="hasError ? 'true' : undefined"
        :aria-describedby="hasError ? errorId : undefined"
        :readonly="readonly"
        @input="handleInputFn"
      />
      <slot name="input-right"></slot>

      <div class="absolute right-0 bottom-0 flex items-center pb-2">
        <button
          v-if="canBeResetted && modelValue.length > 0"
          class="bg-transparent p-1"
          type="button"
          @click="resetInput"
        >
          <fa-icon class="text-rhino size-4" :icon="faTimes" />
        </button>
        <slot name="action"> </slot>
      </div>
    </div>

    <slot name="hint">
      <p
        v-if="hasError"
        :id="errorId"
        class="text-sm mb-0 mt-2 text-danger-fg"
        :class="{ capitalize: !preserveCase }"
      >
        {{ error }}
      </p>
      <p
        v-else-if="hint != null && hint.length > 0"
        class="text-sm mb-0 mt-2 text-rhino"
        :class="{ capitalize: !preserveCase }"
      >
        {{ hint }}
      </p>
    </slot>
  </div>
</template>

<script setup lang="ts">
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { useDebounceFn } from '@vueuse/shared';

const props = withDefaults(
  defineProps<{
    modelValue: string;
    type?: 'text' | 'password' | 'email';
    autocomplete?:
      | 'off'
      | 'on'
      | 'name'
      | 'given-name'
      | 'family-name'
      | 'email'
      | 'username'
      | 'new-password'
      | 'current-password'
      | 'one-time-code'
      | 'country'
      | 'postal-code'
      | 'address-line1'
      | 'address-line2'
      | 'address-level1'
      | 'address-level2';
    inputId?: string;
    placeholder?: string | null;
    label?: string | null;
    hint?: string | null;
    debounce?: number;
    canBeResetted?: boolean;
    transparent?: boolean;
    error?: string | null;
    inputClass?: string | string[];
    readonly?: boolean;
    required?: boolean;
    preserveCase?: boolean;
  }>(),
  {
    type: 'text',
    inputId: undefined,
    autocomplete: undefined,
    placeholder: null,
    label: null,
    hint: null,
    debounce: 0,
    canBeResetted: false,
    transparent: false,
    error: null,
    inputClass: 'text-mouse',
    readonly: false,
    required: false,
    preserveCase: false,
  },
);

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

const componentId = props.inputId || useId();
const errorId = `${componentId}-error`;

const hasError = computed(() => props.error != null && props.error.length > 0);

const handleInput = (event: Event) => {
  emit('update:modelValue', (event.target as HTMLInputElement).value);
};

const handleInputFn =
  props.debounce === 0
    ? handleInput
    : useDebounceFn(handleInput, props.debounce);

const resetInput = () => {
  emit('update:modelValue', '');
};
</script>
